import logger from './debug.js';
import { summarizePrivacy } from './privacy.js';
import { fetchAd } from './request/index.js';
import { fireSyncFrame } from './sync.js';
import {
  ContainerManager,
  CONTAINER_TYPE_CONTENT_LOCK,
  CONTENT_LOCK_GATE,
} from './containers/index.js';
import { resolveDefaultTarget, findPushSite, insertWrapper, renderAd } from './render/index.js';
import { startViewabilityTracking } from './viewability.js';
import { createSurfaceController, contentSize } from './surface/index.js';
import {
  dispatchPublisherEvent,
  LIFECYCLE_REQUEST_KIND,
  lifecycleDetail,
  NPLUSONE_EVENT,
} from './lifecycle.js';

const containerManager = new ContainerManager();
const MAX_SUCCESSFUL_ALTERNATES = 2;

/** Compact ancestor chain for debug logs: "div#slot > div.page > body". */
function ancestorTrail(el) {
  const parts = [];
  for (let node = el.parentElement; node && parts.length < 6; node = node.parentElement) {
    parts.push(`${node.tagName.toLowerCase()}${node.id ? `#${node.id}` : ''}`);
  }
  return parts.join(' > ');
}

function fireEligibleSync(adResponse, privacySnapshot) {
  const localAllowed = privacySnapshot.capabilities.trackCookie
    && privacySnapshot.capabilities.useAdIdentity;
  const serverAllowed = !adResponse.privacy
    || (adResponse.privacy.trackCookie && adResponse.privacy.useAdIdentity);
  if (adResponse.syncFrameUrl && localAllowed && serverAllowed)
    fireSyncFrame(adResponse.syncFrameUrl);
}

function styleBidWrapper(wrapper, bid) {
  if (bid.content.w > 0) {
    wrapper.style.width = '100%';
    wrapper.style.maxWidth = `${bid.content.w}px`;
  }
  wrapper.style.margin = '0 auto';
}

function startBidViewability(wrapper, bid, adResponse, privacySnapshot) {
  if (!bid.viewabilityUrl) return null;
  const serverAllowsTrackingCookie = !adResponse.privacy
    || adResponse.privacy.trackCookie;
  return startViewabilityTracking(wrapper, bid.viewabilityUrl, {
    allowCredentials: privacySnapshot.capabilities.trackCookie
      && serverAllowsTrackingCookie,
  });
}

/**
 * Runs the full fetch → render → sync pipeline for a single placement.
 *
 * Errors at any stage are caught, logged, and returned so they do not
 * affect other placements running in parallel.
 *
 * @param {{ placementId: string }} entry
 * @param {object} renderEnv             - from getRenderEnv()
 * @param {HTMLScriptElement | null} bootstrapScript
 * @param {Promise<object>} privacyPromise - shared live `@neuronic/privacy` controller
 * @param {Promise<object | undefined>} deviceSuaPromise - shared bounded UA Client Hints
 * @param {boolean} forceBid             - dev only: forces a synthetic winning bid
 * @param {HTMLScriptElement | null} pushScript - the publisher's push snippet,
 *                                        captured at push time (post-load pushes only)
 * @returns {Promise<Error | void>}
 */
export async function handlePlacement(entry, renderEnv, bootstrapScript, privacyPromise, deviceSuaPromise, forceBid = false, endpointOverride = null, pushScript = null) {
  const { placementId } = entry;

  if (!placementId) {
    logger.for('placement').error('entry missing placementId — skipping', entry);
    return;
  }

  const plog = logger.for(placementId);

  plog.info('init');

  // ── Privacy ────────────────────────────────────────────────────────────────

  let privacy;
  try {
    privacy = await privacyPromise;
  } catch (err) {
    plog.error('privacy resolution failed — skipping placement', err);
    return err;
  }
  const privacySnapshot = privacy.snapshot();

  plog.debug('privacy ready', summarizePrivacy(privacySnapshot));

  // ── Fetch ──────────────────────────────────────────────────────────────────

  let adResponse;
  let surfaceController;
  const deviceSua = await deviceSuaPromise;

  try {
    surfaceController = await createSurfaceController(renderEnv);
    adResponse = await fetchAd({
      placementId,
      privacySnapshot,
      renderEnv,
      surfaceCapabilities: surfaceController.capabilities(),
      deviceSua,
      forceBid,
      endpointOverride,
    });
  } catch (err) {
    surfaceController?.destroy();
    plog.error('ad fetch failed', err);
    dispatchPublisherEvent(
      renderEnv,
      NPLUSONE_EVENT.Failed,
      lifecycleDetail(placementId),
    );
    return err;
  }

  // ── Sync ───────────────────────────────────────────────────────────────────

  // Fire on every response regardless of fill — improves match rates on no-fill pages.
  fireEligibleSync(adResponse, privacySnapshot);

  // ── No fill ────────────────────────────────────────────────────────────────

  if (!adResponse.hasBid()) {
    surfaceController.destroy();
    plog.debug('no fill');
    dispatchPublisherEvent(
      renderEnv,
      NPLUSONE_EVENT.NoFill,
      lifecycleDetail(placementId),
    );
    return;
  }

  let activeBid = adResponse.bid;

  // ── Safety guards ──────────────────────────────────────────────────────────

  if (!surfaceController.canSatisfy(activeBid.surface, contentSize(activeBid.content))) {
    surfaceController.destroy();
    plog.warn('bid surface requirements cannot be satisfied — skipping render');
    dispatchPublisherEvent(
      renderEnv,
      NPLUSONE_EVENT.Failed,
      lifecycleDetail(placementId, activeBid.instanceId),
    );
    return;
  }

  // ── Container + render ────────────────────────────────────────────────────

  let container = null;
  let wrapper = null;
  let viewabilityStop = null;
  let activeResponse = adResponse;
  const shownSourceIds = new Set(activeBid.sourceId ? [activeBid.sourceId] : []);
  let successfulAlternates = 0;
  let alternatePending = false;

  const emitAlternateNoFill = () => {
    dispatchPublisherEvent(
      renderEnv,
      NPLUSONE_EVENT.NoFill,
      lifecycleDetail(
        placementId,
        activeBid.instanceId,
        null,
        LIFECYCLE_REQUEST_KIND.Alternate,
      ),
    );
  };

  const requestAlternate = async () => {
    if (alternatePending) return;
    if (successfulAlternates >= MAX_SUCCESSFUL_ALTERNATES || !activeBid.sourceId) {
      emitAlternateNoFill();
      return;
    }

    alternatePending = true;
    try {
      const response = await fetchAd({
        placementId,
        privacySnapshot: privacy.snapshot(),
        renderEnv,
        surfaceCapabilities: surfaceController.capabilities(),
        deviceSua,
        forceBid,
        endpointOverride,
        excludedSourceIds: [...shownSourceIds],
      });
      fireEligibleSync(response, privacy.snapshot());

      if (!response.hasBid()) {
        emitAlternateNoFill();
        return;
      }

      const candidate = response.bid;
      const isUsableResearch = candidate.sourceId
        && !shownSourceIds.has(candidate.sourceId)
        && candidate.container.type === CONTAINER_TYPE_CONTENT_LOCK
        && candidate.container.config?.gate?.type === CONTENT_LOCK_GATE.Required
        && surfaceController.canSatisfy(candidate.surface, contentSize(candidate.content));
      if (!isUsableResearch) {
        plog.warn('alternate auction returned an unusable content-lock candidate');
        emitAlternateNoFill();
        return;
      }

      const candidateWrapper = wrapper.ownerDocument.createElement('div');
      if (!await surfaceController.prepare(candidate.surface, contentSize(candidate.content))) {
        plog.warn('alternate surface preparation failed');
        emitAlternateNoFill();
        return;
      }
      styleBidWrapper(candidateWrapper, candidate);
      container.applyStyles(candidateWrapper);
      const replaced = await container.stageReplacement({
        instanceId: candidate.instanceId,
        sourceId: candidate.sourceId,
        wrapper: candidateWrapper,
        render: () => renderAd(
          candidate.content,
          candidate.requiresDirectSurface,
          renderEnv,
          candidateWrapper,
        ),
      });
      if (!replaced) {
        emitAlternateNoFill();
        return;
      }

      viewabilityStop?.();
      wrapper = candidateWrapper;
      surfaceController.bind(wrapper);
      activeBid = candidate;
      activeResponse = response;
      shownSourceIds.add(candidate.sourceId);
      successfulAlternates += 1;
      viewabilityStop = startBidViewability(
        wrapper,
        activeBid,
        activeResponse,
        privacy.snapshot(),
      );
      plog.info('alternate survey rendered', {
        instanceId: activeBid.instanceId,
        successfulAlternates,
      });
    } catch (error) {
      plog.warn('alternate survey unavailable', error);
      emitAlternateNoFill();
    } finally {
      alternatePending = false;
    }
  };

  try {
    container = containerManager.getInstance(
      activeBid.container.type,
      activeBid.container.config,
      renderEnv,
    );

    if (!container.isSupported()) {
      surfaceController.destroy();
      plog.warn('container not supported for current render environment — skipping render');
      dispatchPublisherEvent(
        renderEnv,
        NPLUSONE_EVENT.Failed,
        lifecycleDetail(placementId, activeBid.instanceId),
      );
      return;
    }

    if (container.debugInfo) plog.debug('container resolved', container.debugInfo);

    // Anchor precedence for in_place: push site → bootstrap script (when
    // not head-loaded) → body. See resolveDefaultTarget for the why.
    const pushSite      = findPushSite(placementId, pushScript);
    const defaultTarget = resolveDefaultTarget(
      renderEnv,
      bootstrapScript,
      pushSite,
    );
    const target        = container.renderLocation ?? defaultTarget;
    wrapper             = target.ownerDocument.createElement('div');

    plog.debug('anchor resolved', {
      via: container.renderLocation
        ? 'container-config'
        : pushSite && defaultTarget === pushSite
          ? 'push-site'
          : defaultTarget === bootstrapScript
            ? 'bootstrap-script'
            : defaultTarget === document.body
              ? 'body-fallback'
              : 'document-fallback',
      anchor: `<${target.tagName.toLowerCase()}>${target.id ? `#${target.id}` : ''}`,
      parent: target.parentElement
        ? `<${target.parentElement.tagName.toLowerCase()}>${target.parentElement.id ? `#${target.parentElement.id}` : ''}`
        : null,
    });

    // Width is capped, not pinned — lets responsive creatives (e.g. chattable)
    // shrink with narrow viewports. Fixed-size banners keep their declared
    // size up to the cap; their srcdoc iframe is pixel-sized separately in
    // renderAd and behaves identically to before.
    styleBidWrapper(wrapper, activeBid);

    // Height intentionally not pinned. Banners self-size via the inner
    // iframe's intrinsic w/h attributes; chattable self-sizes via its
    // shadow-DOM CSS and may grow during interaction. Pinning the wrapper
    // height would either be redundant (banner) or actively constrain
    // growth (chattable) — neither is desirable. SurfaceController negotiates
    // and verifies any required slot growth before the creative is rendered.

    container.applyStyles(wrapper);

    if (!await surfaceController.prepare(activeBid.surface, contentSize(activeBid.content))) {
      throw new Error('Surface preparation did not achieve the required initial size');
    }

    // Insert wrapper into the DOM before rendering so that creative
    // scripts can find sibling elements and document.currentScript
    // resolves correctly during execution.
    if (typeof container.insertWrapper === 'function')
      container.insertWrapper(wrapper);
    else
      insertWrapper(wrapper, target);

    surfaceController.bind(wrapper);

    container.prepareLifecycle?.({
      instanceId: activeBid.instanceId,
      sourceId: activeBid.sourceId,
      placementId,
      wrapper,
      log: plog,
      onAlternateRequested: requestAlternate,
    });

    try {
      renderAd(activeBid.content, activeBid.requiresDirectSurface, renderEnv, wrapper);
      if (!await surfaceController.verify(contentSize(activeBid.content)))
        throw new Error('Rendered creative did not satisfy its negotiated surface');
    } catch (renderErr) {
      container.abort?.();
      wrapper.remove();
      throw renderErr;
    }

    container.markRendered?.();

    plog.info('rendered', {
      type: activeBid.container.type,
      surfaceInitial: activeBid.surface.initial,
      surfaceInteraction: activeBid.surface.interaction,
    });

    // Render forensics (debug builds/pages only): where the wrapper actually
    // landed and whether it has a box. A zero-area rect here is the earliest
    // possible signal of an anchoring/visibility problem — long before the
    // viewability safety timeout or a publisher's fallback timer notices.
    const rect = wrapper.getBoundingClientRect();
    plog.debug('wrapper placed', {
      parents: ancestorTrail(wrapper),
      inHead: !!wrapper.closest('head'),
      rect: { x: Math.round(rect.x), y: Math.round(rect.y), w: Math.round(rect.width), h: Math.round(rect.height) },
    });

    viewabilityStop = startBidViewability(wrapper, activeBid, activeResponse, privacySnapshot);
    if (viewabilityStop) {
      plog.info('viewability tracking started');
    }
  } catch (err) {
    surfaceController.destroy();
    container?.abort?.();
    wrapper?.remove();
    plog.error('render pipeline failed — aborting placement', err);
    dispatchPublisherEvent(
      renderEnv,
      NPLUSONE_EVENT.Failed,
      lifecycleDetail(placementId, activeBid.instanceId),
    );
    return err;
  }
}
